// list of payments
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PaymentDetails from './PaymentDetails';

const PaymentList = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  // Mock data for payments
  const mockPayments = [
    {
      id: 1,
      customerName: 'TechCorp Solutions',
      customerEmail: 'john@techcorp.com',
      invoiceNumber: 'INV-2024-001',
      amount: 299.99,
      paymentMethod: 'credit_card',
      cardLast4: '1234',
      status: 'completed',
      transactionId: 'TXN-123456789',
      paymentDate: '2024-01-15 14:30:00',
      processedAt: '2024-01-15 14:31:00',
      description: 'Basic Plan - Monthly Subscription',
      currency: 'USD',
      fee: 8.99,
      netAmount: 291.00,
      gateway: 'Stripe',
      gatewayResponse: 'succeeded'
    },
    {
      id: 2,
      customerName: 'Global Industries',
      customerEmail: 'jane@global.com',
      invoiceNumber: 'INV-2024-002',
      amount: 99.99,
      paymentMethod: 'bank_transfer',
      cardLast4: null,
      status: 'pending',
      transactionId: 'TXN-987654321',
      paymentDate: '2024-01-15 10:15:00',
      processedAt: null,
      description: 'Basic Plan - Monthly Subscription',
      currency: 'USD',
      fee: 2.99,
      netAmount: 97.00,
      gateway: 'Bank Transfer',
      gatewayResponse: 'pending'
    },
    {
      id: 3,
      customerName: 'StartupXYZ',
      customerEmail: 'mike@startupxyz.com',
      invoiceNumber: 'INV-2024-003',
      amount: 149.95,
      paymentMethod: 'credit_card',
      cardLast4: '5678',
      status: 'failed',
      transactionId: 'TXN-456789123',
      paymentDate: '2024-01-14 16:45:00',
      processedAt: '2024-01-14 16:46:00',
      description: 'Basic Plan - Monthly Subscription',
      currency: 'USD',
      fee: 4.49,
      netAmount: 145.46,
      gateway: 'Stripe',
      gatewayResponse: 'card_declined'
    },
    {
      id: 4,
      customerName: 'Innovate Corp',
      customerEmail: 'sarah@innovate.com',
      invoiceNumber: 'INV-2024-004',
      amount: 499.99,
      paymentMethod: 'credit_card',
      cardLast4: '9012',
      status: 'completed',
      transactionId: 'TXN-789123456',
      paymentDate: '2024-01-14 12:20:00',
      processedAt: '2024-01-14 12:21:00',
      description: 'Advance Plan - Monthly Subscription',
      currency: 'USD',
      fee: 14.99,
      netAmount: 485.00,
      gateway: 'Stripe',
      gatewayResponse: 'succeeded'
    },
    {
      id: 5,
      customerName: 'TechCorp Solutions',
      customerEmail: 'john@techcorp.com',
      invoiceNumber: 'INV-2024-005',
      amount: 29.99,
      paymentMethod: 'paypal',
      cardLast4: null,
      status: 'completed',
      transactionId: 'TXN-321654987',
      paymentDate: '2024-01-13 09:30:00',
      processedAt: '2024-01-13 09:31:00',
      description: 'Additional Credits',
      currency: 'USD',
      fee: 1.49,
      netAmount: 28.50,
      gateway: 'PayPal',
      gatewayResponse: 'succeeded'
    }
  ];

  useEffect(() => {
    setPayments(mockPayments);
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleMethodFilter = (event) => {
    setMethodFilter(event.target.value);
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setPaymentDetailsOpen(true);
  };

  const handleResendReceipt = (paymentId) => {
    setSnackbar({
      open: true,
      message: 'Receipt sent successfully',
      severity: 'success'
    });
  };

  const handleRefundPayment = (paymentId) => {
    setSnackbar({
      open: true,
      message: 'Refund processed successfully',
      severity: 'success'
    });
  };

  const handleExportPayments = () => {
    setSnackbar({
      open: true,
      message: 'Payments exported successfully',
      severity: 'success'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
      refunded: 'info',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'failed':
        return <ErrorIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'credit_card':
        return <CreditCardIcon />;
      case 'bank_transfer':
        return <BankIcon />;
      case 'paypal':
        return <PaymentIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const getMethodLabel = (method) => {
    const labels = {
      credit_card: 'Credit Card',
      bank_transfer: 'Bank Transfer',
      paypal: 'PayPal',
      check: 'Check',
      cash: 'Cash'
    };
    return labels[method] || method;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.transactionId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    const matchesMethod = methodFilter === 'all' || payment.paymentMethod === methodFilter;
    return matchesSearch && matchesStatus && matchesMethod;
  });

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2" fontWeight="bold">
          Payment History
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportPayments}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {/* Handle refresh */}}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by customer, invoice, or transaction ID..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilter}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={methodFilter}
                  label="Payment Method"
                  onChange={handleMethodFilter}
                >
                  <MenuItem value="all">All Methods</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {/* Handle advanced filters */}}
                >
                  Advanced Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow 
                    key={payment.id}
                    onClick={() => handleViewPayment(payment)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {payment.customerName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {payment.customerEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {payment.invoiceNumber}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {payment.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(payment.amount)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Net: {formatCurrency(payment.netAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getMethodIcon(payment.paymentMethod)}
                        <Box>
                          <Typography variant="body2">
                            {getMethodLabel(payment.paymentMethod)}
                          </Typography>
                          {payment.cardLast4 && (
                            <Typography variant="caption" color="textSecondary">
                              **** {payment.cardLast4}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(payment.status)}
                        label={payment.status.toUpperCase()}
                        color={getStatusColor(payment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {payment.transactionId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(payment.paymentDate).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewPayment(payment);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {payment.status === 'completed' && (
                          <>
                            <Tooltip title="Resend Receipt">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResendReceipt(payment.id);
                                }}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Refund Payment">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRefundPayment(payment.id);
                                }}
                              >
                                <ReceiptIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Payment Details Modal */}
      <PaymentDetails
        open={paymentDetailsOpen}
        onClose={() => setPaymentDetailsOpen(false)}
        payment={selectedPayment}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentList;